/**
 * Page d'achat d'un PACK de SKILLCARDS (migration 0337) — `/pack/$packId/acheter`.
 *
 * Ouverte par « Se tester » d'un pack non débloqué (« Mon année ») : propose le
 * PACK entier, puis le programme de l'année qui le contient, et l'accès complet
 * en alternative. Mêmes briques que la page d'achat d'une carte
 * (`SkillcardOfferRow`, deck, `SubscriptionAlternative`, retour Stripe
 * `PurchaseResult`). Prix calculés par la base (`get_pack_offers`, cartes déjà
 * possédées déduites). Stripe revient ici (`from_pack_id`) puis `?return=`.
 *
 * Publique (enfant de rootRoute) : prix visibles sans compte, connexion sur la
 * page. Natif : achat intégré (palier Google Play / App Store, 0351), jamais de lien Stripe.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { useParams, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Layers, Library } from 'lucide-react';
import { Button, MathText, PackIconGlyph } from '@jeraisonne/ui';
import { OrigamiCreature } from '@jeraisonne/ui/tool-cards';
import {
  CARD_RARITY_STARS,
  cardPalette,
  formatEuros,
  type CardRarity,
  type SkillcardOfferKind,
  offerContentLabel,
} from '@jeraisonne/db/cards';
import { levelLabel, packPeriodLabel, subjectLabel } from '@jeraisonne/db/curriculum';
import { useAuth } from '@/lib/auth';
import { canBuySkillcardsInApp, storePaymentNote, usesStoreBilling } from '@/lib/skillcards/iap';
import { OtpAuthForm } from '@/components/otp-auth-form';
import { setReturnTo } from '@/lib/return-to';
import {
  packBuyPath,
  packUnlockedLabel,
  safeInternalPath,
  skillcardCheckoutErrorMessage,
  startSkillcardCheckout,
  usePackOffers,
  type PackOffers,
} from '@/lib/skillcards/purchase';
import { Section, SubscriptionAlternative } from '@/features/skillcards/skillcard-offers-panel';
import { PackCardFan } from '@/features/skillcards/pack-card-fan';
import { DeckNote } from '@/features/skillcards/deck-note';
import { SkillcardOfferRow } from '@/features/skillcards/skillcard-offer-row';
import { PurchaseResult } from '@/routes/card.$cardId.acheter';
import { SkillcardPromoBanner } from '@/features/skillcards/skillcard-promo-banner';

const DEFAULT_RETURN = '/student/progression';

export function PackBuyPage() {
  const { packId } = useParams({ strict: false }) as { packId: string };
  const search = useSearch({ strict: false }) as { return?: string; purchase?: string };
  const { session, profile } = useAuth();
  // Sans `?return=` : l'espace de l'appelant (un Curieux n'a pas « Mon année »).
  const fallbackReturn = profile?.role === 'player' ? '/player/skillcards' : DEFAULT_RETURN;
  const returnPath = safeInternalPath(search.return);
  const userId = session?.user?.id ?? null;
  const offers = usePackOffers(packId, userId);
  const [showLogin, setShowLogin] = useState(false);
  // App native : achat via Google Play / App Store (paliers, 0351).
  const native = !canBuySkillcardsInApp();
  const data = offers.data;

  useEffect(() => {
    if (data) document.title = `Débloquer le pack ${data.pack.title} · JeRaisonne`;
  }, [data]);

  if (search.purchase) {
    const firstCard = data?.cards[0]?.id ?? '';
    const back = returnPath ?? fallbackReturn;
    return (
      <PurchaseResult
        cardId={firstCard}
        purchaseId={search.purchase}
        returnPath={returnPath}
        retryHref={packBuyPath(packId, returnPath)}
        target={back}
        doneLabel={back === DEFAULT_RETURN ? 'Retour à mon année' : 'Continuer'}
      />
    );
  }

  const unlocked = packUnlockedLabel(data?.access);
  const signedIn = !!session && !!profile;

  return (
    <div className="native:pt-[calc(env(safe-area-inset-top)+0.75rem)] min-h-dvh px-4 pb-[max(env(safe-area-inset-bottom),1.5rem)] pt-3 sm:py-8">
      <div className="mx-auto max-w-xl space-y-4">
        <a
          href={returnPath ?? fallbackReturn}
          className="text-ink-500 hover:text-ink-800 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Retour
        </a>

        {offers.isLoading ? (
          <div className="bg-ink-100 h-40 animate-pulse rounded-2xl" />
        ) : !data ? (
          <p className="text-ink-600 text-sm">Ce pack n’est pas disponible.</p>
        ) : (
          <>
            <PackBuyHero data={data} />

            {unlocked && (
              <div className="bg-success-50 text-success-800 rounded-2xl p-4 text-sm">
                <p className="font-semibold">{unlocked}.</p>
              </div>
            )}

            {native ? (
              <div className="bg-ink-50 text-ink-700 rounded-2xl p-4 text-sm">
                Les packs de SKILLCARDS s’achètent sur le site jeraisonne.fr. Ils sont aussi inclus
                dans l’abonnement JeRaisonne.
              </div>
            ) : (
              <>
                {!signedIn && (
                  <div className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4">
                    <p className="text-ink-700 text-sm">
                      Connecte-toi d’abord pour pouvoir acheter : les cartes s’ajoutent à ton deck.
                    </p>
                    {showLogin ? (
                      <OtpAuthForm
                        renderHeader={() => (
                          <p className="text-ink-900 font-display text-base font-bold">
                            Connexion ou création de compte — tu reviendras sur cette page
                          </p>
                        )}
                      />
                    ) : (
                      <Button
                        fullWidth
                        onClick={() => {
                          setReturnTo(`/pack/${packId}/acheter`);
                          setShowLogin(true);
                        }}
                      >
                        Se connecter
                      </Button>
                    )}
                  </div>
                )}
                <PackOffersPanel
                  data={data}
                  userId={userId}
                  signedIn={signedIn}
                  returnPath={returnPath ?? fallbackReturn}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Cover : l'éventail des SKILLCARDS du pack (créatures origami) sur un fond dans
 * la teinte du NIVEAU, le titre et la période ; puis la liste de ses cartes.
 */
export function PackBuyHero({ data }: { data: PackOffers }) {
  const { pack, cards } = data;
  const palette = cardPalette([pack.level], 'level');
  // Teinte du niveau en variables CSS (seules valeurs non statiques).
  const vars = { '--jr-c-accent': palette.accent, '--jr-c-bg': palette.bg } as CSSProperties;
  return (
    <div className="space-y-3">
      <div
        data-testid="pack-buy-hero"
        style={vars}
        className="relative overflow-hidden rounded-3xl bg-[radial-gradient(circle_at_50%_30%,color-mix(in_srgb,var(--jr-c-accent)_55%,var(--jr-c-bg))_0%,var(--jr-c-bg)_70%)] px-4 pb-6 pt-4 text-center shadow-[0_18px_50px_-20px_rgba(0,0,0,0.6)] ring-1 ring-[color-mix(in_srgb,var(--jr-c-accent)_45%,transparent)]"
      >
        <p className="font-display text-sm font-extrabold uppercase tracking-[0.22em] text-[var(--jr-c-accent)] sm:text-base">
          Débloque le pack
        </p>
        <div className="mt-4">
          <PackCardFan cards={cards} />
        </div>
        <p className="font-display mx-auto mt-4 inline-flex items-center gap-1.5 rounded-full border border-[var(--jr-c-accent)] bg-[color-mix(in_srgb,var(--jr-c-accent)_25%,transparent)] px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-white">
          <PackIconGlyph icon={pack.icon} className="h-3.5 w-3.5" />
          {cards.length} SKILLCARD{cards.length > 1 ? 'S' : ''}
        </p>
        <h1 className="font-display mt-3 text-xl font-extrabold leading-tight text-white drop-shadow sm:text-2xl">
          {pack.title}
        </h1>
        <p className="mt-1 text-sm font-semibold text-white/75">
          {levelLabel(pack.level)} · {subjectLabel(pack.subject)} ·{' '}
          {packPeriodLabel(pack.start_month, pack.end_month)}
        </p>
        {pack.hook && <p className="mx-auto mt-2 max-w-sm text-sm text-white/80">{pack.hook}</p>}
      </div>

      {cards.length > 0 && <PackCardList cards={cards} />}
    </div>
  );
}

/** Nombre de cartes visibles avant « Voir les N cartes » : l'offre reste près du haut. */
export const PACK_CARDS_PREVIEW = 3;

function PackCardList({ cards }: { cards: PackOffers['cards'] }) {
  const [all, setAll] = useState(false);
  const shown = all ? cards : cards.slice(0, PACK_CARDS_PREVIEW);
  return (
    <div className="space-y-2">
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2" aria-label="SKILLCARDS du pack">
        {shown.map((c) => {
          const p = cardPalette(c.levels, c.color_theme);
          const stars = CARD_RARITY_STARS[c.rarity as CardRarity] ?? 0;
          return (
            <li key={c.id}>
              <a
                href={`/card/${c.id}`}
                className="border-ink-100 ocean:bg-white/5 flex min-h-[2.75rem] items-center gap-2 rounded-xl border bg-white p-2 active:scale-[0.99]"
              >
                <span
                  aria-hidden
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border"
                  style={{ background: p.bg, borderColor: p.accent }}
                >
                  <OrigamiCreature creature={c.creature} accent={p.accent} className="h-8 w-8" />
                </span>
                <span className="min-w-0">
                  <span className="text-ink-900 block truncate text-sm font-semibold">
                    <MathText>{c.title}</MathText>
                  </span>
                  <span className="text-xs tracking-wider text-amber-500" aria-hidden>
                    {'★'.repeat(stars)}
                  </span>
                </span>
              </a>
            </li>
          );
        })}
      </ul>
      {cards.length > PACK_CARDS_PREVIEW && (
        <button
          type="button"
          aria-expanded={all}
          onClick={() => setAll((v) => !v)}
          className="text-primary-600 min-h-[2.75rem] text-sm font-semibold"
        >
          {all ? 'Voir moins' : `Voir les ${cards.length} cartes du pack`}
        </button>
      )}
    </div>
  );
}

/** Offres : le pack, puis le programme de l'année, le deck, l'accès complet. */
function PackOffersPanel({
  data,
  userId,
  signedIn,
  returnPath,
}: {
  data: PackOffers;
  userId: string | null;
  signedIn: boolean;
  returnPath: string;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cardId = data.cards[0]?.id ?? null;

  async function buy(kind: SkillcardOfferKind, targetId: string) {
    if (!cardId) return;
    setError(null);
    setBusy(`${kind}:${targetId}`);
    try {
      const url = await startSkillcardCheckout({
        kind,
        targetId,
        cardId,
        returnPath,
        fromPackId: data.pack.id,
      });
      window.location.href = url;
    } catch (e) {
      setError(skillcardCheckoutErrorMessage((e as Error).message));
      setBusy(null);
    }
  }

  const pack = data.pack_offer;
  const programme = data.programme_offer;
  return (
    <div className="space-y-4">
      <SkillcardPromoBanner />
      {error && (
        <p role="alert" className="text-error-600 text-sm">
          {error}
        </p>
      )}

      {pack && (
        <Section icon={<Layers className="h-4 w-4" />} title="Le pack entier">
          <SkillcardOfferRow
            quote={pack}
            title={pack.label}
            subtitle={`${offerContentLabel(pack)} · ${levelLabel(data.pack.level)} · ${subjectLabel(data.pack.subject)}`}
            highlight="Pour s’entraîner sur ce pack"
            busy={busy === `pack:${pack.target_id}`}
            disabled={!signedIn || !!busy || !cardId}
            onBuy={() => void buy('pack', pack.target_id)}
          />
        </Section>
      )}

      {programme && (
        <Section icon={<Library className="h-4 w-4" />} title="Tout le programme de l’année">
          <SkillcardOfferRow
            quote={programme}
            title={`${levelLabel(data.pack.level)} · ${subjectLabel(data.pack.subject)}`}
            subtitle={`${offerContentLabel(programme)} — tous les packs de l’année`}
            highlight="Le plus avantageux"
            busy={busy === `programme:${programme.target_id}`}
            disabled={!signedIn || !!busy || !cardId}
            onBuy={() => void buy('programme', programme.target_id)}
          />
        </Section>
      )}

      <DeckNote userId={userId} validityMonths={data.settings.validity_months} />

      {!usesStoreBilling() && <SubscriptionAlternative signedIn={signedIn} returnTo={returnPath} />}

      <p className="text-ink-500 text-xs leading-relaxed">
        {usesStoreBilling() ? (
          storePaymentNote()
        ) : (
          <>
            Paiement unique sécurisé par Stripe, sans abonnement. Prix unitaire :{' '}
            {formatEuros(data.settings.unit_price_cents)} la carte.
          </>
        )}
      </p>
    </div>
  );
}
